import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { AspectRatios } from 'common/presentation/aspect-ratios'
import { FC, useMemo } from 'react'
import useResize from 'utils/hooks/use-resize'
import { SceneVideoUser } from 'video-conference-media'
import { SceneVideoUserStream } from 'video-conference-media/presentation/scene-video-user-stream'

const Container = styled.div`
	flex: 1;
	display: flex;
	min-height: 0;
	width: 100%;
`

const VideoStreamColumnDiv = styled.div`
	display: flex;
	flex-flow: column nowrap;
	justify-content: center;
	align-items: center;
`

const videoMargin = 5

const calculateVideoSize = (
	containerWidth: number,
	containerHeight: number,
	numberOfVideos: number
) => {
	const dividedHeight = Math.floor(
		(containerHeight - numberOfVideos * videoMargin * 2) / numberOfVideos
	)
	const effectiveContainerWidth = containerWidth - 2 * videoMargin
	const percentageEffectiveContainerWidth =
		(effectiveContainerWidth / containerWidth) * 100
	const aspectedWidth = dividedHeight * AspectRatios.standardVideo
	if (aspectedWidth >= effectiveContainerWidth) {
		const height = effectiveContainerWidth / AspectRatios.standardVideo
		return {
			width: `${percentageEffectiveContainerWidth}%`,
			height: `${height}px`,
		}
	}
	return {
		width: `${aspectedWidth}px`,
		height: `${dividedHeight}px`,
	}
}

type VideoStreamColumnProps = Pick<Props, 'sceneVideoUsers'> & {
	width: string
}

const maxNumberOfStreamsInColumn = 8

const VideoStreamColumn: FC<VideoStreamColumnProps> = ({
	width,
	sceneVideoUsers,
}) => {
	const {
		ref: containerRef,
		width: containerWidth,
		height: containerHeight,
	} = useResize()
	const sizingClass = useMemo(() => {
		const videoSize = calculateVideoSize(
			containerWidth,
			containerHeight,
			Math.min(maxNumberOfStreamsInColumn, sceneVideoUsers.length)
		)
		return css`
			width: ${videoSize.width};
			height: ${videoSize.height};
			margin: ${videoMargin}px;
		`
	}, [containerHeight, containerWidth, sceneVideoUsers.length])

	return (
		<VideoStreamColumnDiv
			css={css`
				width: ${width};
				padding: ${videoMargin}px;
			`}
			ref={containerRef}
		>
			{sceneVideoUsers.map(sceneVideoUser => (
				<SceneVideoUserStream
					key={sceneVideoUser.userId}
					css={sizingClass}
					sceneVideoUser={sceneVideoUser}
				/>
			))}
		</VideoStreamColumnDiv>
	)
}

type Columns = SceneVideoUser[][]

const splitColumns = (sceneVideoUsers: SceneVideoUser[]): Columns => {
	return sceneVideoUsers.reduce((previousValue, currentValue, currentIndex) => {
		const columnIndex = Math.floor(currentIndex / maxNumberOfStreamsInColumn)
		if (previousValue.length <= columnIndex) {
			previousValue.push([])
		}
		previousValue[columnIndex].push(currentValue)
		return previousValue
	}, [] as Columns)
}

type Props = {
	sceneVideoUsers: SceneVideoUser[]
}

export const VideoColumns: FC<Props> = props => {
	const columns = useMemo(
		() => splitColumns(props.sceneVideoUsers),
		[props.sceneVideoUsers]
	)
	return (
		<Container>
			{columns.map((column, index) => (
				<VideoStreamColumn
					key={index}
					width={`${100 / columns.length}%`}
					sceneVideoUsers={column}
				/>
			))}
		</Container>
	)
}
